import React from 'react';
import { createRoot } from 'react-dom/client';

import { type AuthProviderProps } from '@myphx-engineering/auth/client';
import { setupTemporalPolyfill } from '@myphx-engineering/date-utils/client';
import {
    FeatureFlagProvider,
    makeFlagProvider,
} from '@myphx-engineering/feature-flags/client';
import {
    TelemetryProvider,
    type TelemetryProviderProps,
} from '@myphx-engineering/instrumentation/react';
import { Box } from '@myphx-engineering/phx-design-system';
import { ThemeProvider } from '@myphx-engineering/phx-design-system/framework';
import theme from '@myphx-engineering/phx-theme';
import { PwaProvider, type PwaProviderProps } from '@phx/pwa';
import '@myphx-engineering/phx-theme/theme.css';
import './i18n';

// Setup Temporal polyfill before any date operations
setupTemporalPolyfill();

import { AppRouter } from './AppRouter';
import { ErrorBoundary } from './components/common/ErrorBoundary';
import { AppLoadedEventDispatcher } from './components/layout/AppLoadedEventDispatcher';
import { UserPrefsInitializer } from './components/user/UserPrefsInitializer';
import { DatadogInitializer } from './components/utility/DatadogInitializer';
import { DevTools } from './components/utility/dev-tools/DevTools';
import { RootSuspenseBoundary } from './components/utility/RootSuspenseBoundary';
import { AppAuthProvider } from './contexts/AppAuthProvider';
import { AppConfigProvider } from './contexts/AppConfigProvider';
import { ErrorSessionProvider } from './contexts/ErrorSessionProvider';
import { GraphQLProvider } from './contexts/GraphQLProvider';
import { LoadingProvider } from './contexts/LoadingProvider';
import RootErrorRoute from './routes/error/RootErrorRoute';

const authConfig: AuthProviderProps = {
    authUrl: import.meta.env.VITE_AUTH_URL,
    clientId: import.meta.env.VITE_AUTH_CLIENT_ID,
    redirectUri: import.meta.env.VITE_AUTH_REDIRECT_URI,
    rootUrl: '/home',
    tokenUrl: import.meta.env.VITE_AUTH_TOKEN_URL,
    disableHeartbeat: true,
    autoLogout: 30 * 60 * 1000, // 30 minutes
};

const telemetryConfig: TelemetryProviderProps = {
    buildNumber: import.meta.env.VITE_BUILD_NUMBER,
    instrumentationKey: import.meta.env
        .VITE_APPLICATION_INSIGHTS_CONNECTION_STRING,
    cloudRole: 'ux-my-prescryptive',
};

const pwaPath = '/static/pwa';
const pwaIllustrationsPath = `${pwaPath}/illustrations`;

const pwaConfig: PwaProviderProps = {
    pwaId: 'phx',
    appName: 'myPrescryptive',
    pwaAssets: {
        illustrations: {
            addToContacts: `${pwaIllustrationsPath}/add-to-contacts.svg`,
            addToContactsFromMenu: `${pwaIllustrationsPath}/add-to-contacts-from-menu.svg`,
            addToHome: `${pwaIllustrationsPath}/add-to-home.svg`,
            addToHomeFromMenu: `${pwaIllustrationsPath}/add-to-home-from-menu.svg`,
        },
        contactCard: {
            name: 'my-prescryptive.vcf',
            url: `${pwaPath}/vcf/my-prescryptive.vcf`,
        },
    },
};

(async () => {
    const provider = await makeFlagProvider(
        import.meta.env.VITE_LAUNCH_DARKLY_CLIENT_ID,
        undefined,
        import.meta.env.VITE_LAUNCH_DARKLY_BASE_URL
    );

    createRoot(document.getElementById('root')!).render(
        <React.StrictMode>
            <ThemeProvider theme={theme}>
                <ErrorSessionProvider>
                    <ErrorBoundary
                        fallback={
                            <Box p="md">
                                <RootErrorRoute />
                            </Box>
                        }
                    >
                        <TelemetryProvider {...telemetryConfig}>
                            <FeatureFlagProvider provider={provider}>
                                <AppConfigProvider>
                                    <RootSuspenseBoundary>
                                        <PwaProvider {...pwaConfig}>
                                            <AppAuthProvider {...authConfig}>
                                                <GraphQLProvider>
                                                    <LoadingProvider>
                                                        <AppRouter />
                                                    </LoadingProvider>
                                                </GraphQLProvider>
                                            </AppAuthProvider>
                                            <AppLoadedEventDispatcher />
                                        </PwaProvider>
                                        <DatadogInitializer />
                                        <UserPrefsInitializer />
                                    </RootSuspenseBoundary>
                                    {import.meta.env.MODE ===
                                    'production' ? null : (
                                        <DevTools />
                                    )}
                                </AppConfigProvider>
                            </FeatureFlagProvider>
                        </TelemetryProvider>
                    </ErrorBoundary>
                </ErrorSessionProvider>
            </ThemeProvider>
        </React.StrictMode>
    );
})();
